/**
 * @file AuthProvider.tsx
 * @description Провайдер AuthContext — единая точка работы с сессией.
 *
 * При монтировании дёргает GET /auth/me. На 401 — переводит статус в
 * 'unauthenticated'. Других «фатальных» ошибок не ловим: сетевые показываем
 * через `error` поле, но не блокируем рендер (LoginPage всё равно публичный).
 *
 * Слушает CustomEvent `auth:unauthenticated`, который шлёт api-client при
 * любом 401 ответе — это даёт автоматический logout, если сессия упала
 * на сервере (например, истёк TTL Redis).
 *
 * phase19.31 (SC21–SC23): хранит кабинет ЭТОЙ вкладки (`tabContextId`, он
 * же — заголовок `X-Commora-Context` в запросах, `lib/tab-context`) и
 * отдаёт `me.activeContext` именно этого кабинета. Кабинет вкладки не
 * меняет кабинет сессии, поэтому соседние вкладки друг другу не мешают.
 * Кабинет вкладки стал чужим (403 `rbac_tab_context_forbidden` на
 * `/auth/me`) — забываем его и перечитываем профиль по сессии.
 *
 * Явный выход стирает ключи окна «Сообщения» вкладки (черновики, отбор по
 * клиенту) — даже если сервер не ответил (`finally`).
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import {
  AUTH_UNAUTHENTICATED_EVENT,
  ApiError,
  RBAC_NO_ACTIVE_CONTEXT_EVENT,
  apiFetch,
} from "../lib/api-client";
import { getTabContextId, setTabContextId, withTabContext } from "../lib/tab-context";
import { clearChatSession } from "../components/chat/chat-window-state";
import { invalidateSiteAccess } from "../lib/site-access/site-access-store";
import { invalidateLegalTargets } from "../lib/site-view/legal-targets-store";
import { resetShopSiteUrlCache } from "../hooks/useShopSiteUrl";
import { invalidateLiveSiteIssues } from "../lib/site-view/live-issues-store";
import { AuthContext, type AuthContextValue, type AuthStatus } from "./AuthContext";
import type { MeResponse } from "./types";

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [me, setMe] = useState<MeResponse | null>(null);
  // Кабинет вкладки: состояние (для перерисовки) + переменная модуля (для запросов).
  const [tabContextId, setTabContextState] = useState<string | null>(getTabContextId);
  // флаг, чтобы не делать setState после unmount в строгом режиме
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const applyMe = useCallback((value: MeResponse | null) => {
    if (!mountedRef.current) return;
    setMe(value);
    setStatus(value ? "authenticated" : "unauthenticated");
  }, []);

  const selectTabContext = useCallback((contextId: string | null) => {
    // Другой кабинет — плашка «Сайт работает без…» другого магазина (SB120 этап 2).
    invalidateLiveSiteIssues();
    // И плашка «Сайт закрыт» — тоже другого магазина (SB138).
    invalidateSiteAccess();
    // Документы магазина (цели ссылок) и адрес его сайта — тоже другого магазина.
    invalidateLegalTargets();
    resetShopSiteUrlCache();
    setTabContextId(contextId);
    setTabContextState(contextId);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const data = await apiFetch<MeResponse>("/auth/me");
      applyMe(data);
    } catch (err) {
      // Кабинет вкладки больше не наш (доступ сняли, вход под сотрудником
      // завершён) — работаем по сессии и перечитываем профиль ещё раз.
      if (err instanceof ApiError && err.code === "rbac_tab_context_forbidden" && getTabContextId()) {
        selectTabContext(null);
        return refresh();
      }
      if (err instanceof ApiError && err.status === 401) {
        applyMe(null);
        return;
      }
      // Сетевые/5xx — не трогаем сессию, оставляем последнее известное состояние,
      // но если оно loading — сваливаемся в unauthenticated, чтобы не висеть бесконечно.
      if (mountedRef.current && status === "loading") {
        setStatus("unauthenticated");
      }
      throw err;
    }
  }, [applyMe, status, selectTabContext]);

  // первый запрос /me на монтирование. refresh намеренно НЕ в deps:
  // он завязан на текущий status и пересоздаётся, но один раз дёрнуть
  // на маунте — ровно то, что нам нужно.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void refresh(); }, []);

  // глобальный 401-обработчик из api-client
  useEffect(() => {
    function onUnauthenticated() {
      applyMe(null);
    }
    window.addEventListener(AUTH_UNAUTHENTICATED_EVENT, onUnauthenticated);
    return () => window.removeEventListener(AUTH_UNAUTHENTICATED_EVENT, onUnauthenticated);
  }, [applyMe]);

  // 403 rbac_no_active_context — refetch /auth/me, чтобы синхронизировать
  // me.activeContext с серверным session.activeContextId. Если activeContext
  // действительно null — RequireContextType в роутере сам редиректнёт на
  // /select-context.
  useEffect(() => {
    function onNoActiveContext() {
      void refresh();
    }
    window.addEventListener(RBAC_NO_ACTIVE_CONTEXT_EVENT, onNoActiveContext);
    return () =>
      window.removeEventListener(RBAC_NO_ACTIVE_CONTEXT_EVENT, onNoActiveContext);
  }, [refresh]);

  const login = useCallback(
    async (input: { email: string; password: string }) => {
      const data = await apiFetch<MeResponse>("/auth/login", { method: "POST", json: input });
      applyMe(data);
      return data;
    },
    [applyMe],
  );

  const logout = useCallback(async () => {
    try {
      await apiFetch<{ ok: true }>("/auth/logout", { method: "POST" });
    } finally {
      // Явный выход: черновики ответов и состояние окна «Сообщения» (там
      // имя клиента в отборе) не должны пережить выход в этой вкладке.
      clearChatSession();
      selectTabContext(null);
      applyMe(null);
    }
  }, [applyMe, selectTabContext]);

  const switchContext = useCallback(
    async (contextId: string) => {
      const data = await apiFetch<MeResponse>("/auth/switch-context", {
        method: "POST",
        json: { contextId },
      });
      // Явный выбор кабинета (страница «Выбор кабинета») — и для этой вкладки.
      selectTabContext(contextId);
      applyMe(data);
      return data;
    },
    [applyMe, selectTabContext],
  );

  // Профиль глазами вкладки: activeContext — кабинет вкладки.
  const tabMe = useMemo(() => (me ? withTabContext(me, tabContextId) : null), [me, tabContextId]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, me: tabMe, login, logout, switchContext, refresh, tabContextId, selectTabContext }),
    [status, tabMe, login, logout, switchContext, refresh, tabContextId, selectTabContext],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
